import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { compile, EvalFunction } from "mathjs";
import { LogChartOverlaySeries } from "../components/LogChart/LogChart";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { apiSlice } from "../store/slices/apiSlice";
import { CurveMeasure } from "../store/types";

interface CurveSpecification {
  mnemonic: string;
  unit?: string;
}

interface LogChartData {
  curveSpecifications?: CurveSpecification[];
  data?: Record<string, unknown>[];
}

type LogsDataByUid = Record<string, LogChartData | undefined>;

interface ConversionRequest {
  curveName: string;
  unitName: string;
}

/**
 * Кривая, значения которой приходят не из лога (например, из конструкции скважины),
 * но должны быть приведены к активной системе единиц.
 */
export interface ExtraCurveRequest {
  curveName: string;
  /** Единица, в которой приходит значение. Пусто — переводить нечего. */
  unit?: string;
}

interface ConversionDefinition {
  curveName: string;
  sourceUnit: string;
  targetUnit: string;
  expression: EvalFunction;
}

const UNIT_SYSTEM_STORAGE_KEY = "unitSystemId";
// Overlay depths are converted through the same curve the calculated log uses
// for its index, so overlay lines land on the chart's depth axis.
const OVERLAY_DEPTH_CURVE_NAME = "MD";
// Intl.NumberFormat rejects fraction digits above 20.
const MAX_PRECISION = 20;
export const UNIT_SYSTEM_CHANGED_EVENT = "unit-system-changed";
// Stable identity, so callers that pass nothing do not invalidate the request memo.
const NO_EXTRA_CURVES: ExtraCurveRequest[] = [];

const normalizeUnitName = (unit?: string) => unit?.trim() || "";

const shouldConvertUnit = (unit?: string) => {
  const normalized = normalizeUnitName(unit);
  return Boolean(normalized) && normalized.toLowerCase() !== "unitless";
};

const makeConversionKey = (curveName: string, unitName: string) =>
  `${curveName.trim().toLowerCase()}::${unitName.trim().toLowerCase()}`;

const sanitizeFormula = (formula: string) => {
  let sanitized = formula.trim();

  if (sanitized.startsWith("**") && sanitized.endsWith("**")) {
    sanitized = sanitized.slice(2, -2);
  }

  return sanitized
    .replace(/^["']|["']$/g, "")
    .replace(/\\\*/g, "*")
    .replace(/(\d),(\d)/g, "$1.$2");
};

const compileFormula = (formula?: string) => {
  if (!formula) return undefined;

  try {
    return compile(sanitizeFormula(formula));
  } catch {
    return undefined;
  }
};

const getCurveMeasureUnit = (curveMeasure?: CurveMeasure) =>
  curveMeasure?.unitMeasure?.measureLabel?.trim();

const getCurveMeasurePrecision = (curveMeasure?: CurveMeasure) => {
  const precision = Number(curveMeasure?.precision);
  if (!Number.isFinite(precision) || precision < 0) return undefined;
  return Math.min(Math.trunc(precision), MAX_PRECISION);
};

/** Ключ кривой в `curveUnits`/`curvePrecisions` — имя без регистра и краевых пробелов. */
export const makeCurveKey = (curveName: string) => curveName.trim().toLowerCase();

const toFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const convertValue = (
  value: unknown,
  conversion: ConversionDefinition | undefined
) => {
  if (!conversion) return value;

  const number = toFiniteNumber(value);
  if (number === undefined) return value;

  try {
    const converted = conversion.expression.evaluate({ x: number });
    const convertedNumber = Number(converted);
    return Number.isFinite(convertedNumber) ? convertedNumber : value;
  } catch {
    return value;
  }
};

// Measures are requested for every curve, since the precision they carry is
// needed even where no unit conversion applies; formulas only for curves whose
// unit actually has to be converted.
const collectCurveRequests = (
  logsData: LogsDataByUid,
  overlaySeries: LogChartOverlaySeries[],
  extraCurves: ExtraCurveRequest[]
) => {
  const curveNames = new Map<string, string>();
  const conversionRequests = new Map<string, ConversionRequest>();

  const addCurve = (curveName: string) => {
    if (curveName) curveNames.set(makeCurveKey(curveName), curveName);
  };

  const addConversion = (curveName: string, unitName: string) => {
    conversionRequests.set(makeConversionKey(curveName, unitName), {
      curveName,
      unitName,
    });
  };

  Object.values(logsData).forEach((logData) => {
    logData?.curveSpecifications?.forEach((spec) => {
      if (!spec.mnemonic) return;
      addCurve(spec.mnemonic);

      const unitName = normalizeUnitName(spec.unit);
      if (shouldConvertUnit(unitName)) addConversion(spec.mnemonic, unitName);
    });
  });

  overlaySeries.forEach((overlay) => {
    if (overlay.mnemonic) {
      addCurve(overlay.mnemonic);

      const unitName = normalizeUnitName(overlay.unit);
      if (shouldConvertUnit(unitName)) addConversion(overlay.mnemonic, unitName);
    }

    addCurve(OVERLAY_DEPTH_CURVE_NAME);
    const depthUnitName = normalizeUnitName(overlay.depthUnit);
    if (shouldConvertUnit(depthUnitName)) {
      addConversion(OVERLAY_DEPTH_CURVE_NAME, depthUnitName);
    }
  });

  extraCurves.forEach(({ curveName, unit }) => {
    if (!curveName) return;
    addCurve(curveName);

    const unitName = normalizeUnitName(unit);
    if (shouldConvertUnit(unitName)) addConversion(curveName, unitName);
  });

  return {
    curveNames: Array.from(curveNames.values()),
    conversionRequests: Array.from(conversionRequests.values()),
  };
};

const readStoredUnitSystemId = () =>
  window.localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY) || "";

export const useStoredUnitSystemId = () => {
  const [unitSystemId, setUnitSystemId] = useState(readStoredUnitSystemId);

  useEffect(() => {
    const updateUnitSystemId = () => setUnitSystemId(readStoredUnitSystemId());

    window.addEventListener("storage", updateUnitSystemId);
    window.addEventListener(UNIT_SYSTEM_CHANGED_EVENT, updateUnitSystemId);

    return () => {
      window.removeEventListener("storage", updateUnitSystemId);
      window.removeEventListener(UNIT_SYSTEM_CHANGED_EVENT, updateUnitSystemId);
    };
  }, []);

  return unitSystemId;
};

export const useLogChartUnitConversion = (
  logsData: LogsDataByUid,
  overlaySeries: LogChartOverlaySeries[],
  unitSystemId: string,
  extraCurves: ExtraCurveRequest[] = NO_EXTRA_CURVES
) => {
  const dispatch = useAppDispatch();
  const systemsEP = useAppSelector((state) => state.endpoints.systems);
  const convertFormulaEP = useAppSelector(
    (state) => state.endpoints.convertFormula
  );
  const [conversions, setConversions] = useState<
    Record<string, ConversionDefinition>
  >({});
  const [curvePrecisions, setCurvePrecisions] = useState<
    Record<string, number>
  >({});
  const [curveUnits, setCurveUnits] = useState<Record<string, string>>({});
  const [conversionRevision, setConversionRevision] = useState(0);

  const curveRequests = useMemo(
    () => collectCurveRequests(logsData, overlaySeries, extraCurves),
    [logsData, overlaySeries, extraCurves]
  );
  const curveRequestsKey = useMemo(
    () =>
      [
        curveRequests.curveNames.map(makeCurveKey).sort().join("|"),
        curveRequests.conversionRequests
          .map((request) =>
            makeConversionKey(request.curveName, request.unitName)
          )
          .sort()
          .join("|"),
      ].join("::"),
    [curveRequests]
  );
  const curveRequestsRef = useRef(curveRequests);
  curveRequestsRef.current = curveRequests;

  useEffect(() => {
    const { curveNames, conversionRequests } = curveRequestsRef.current;

    if (
      !unitSystemId ||
      !systemsEP ||
      !convertFormulaEP ||
      curveNames.length === 0
    ) {
      setConversions({});
      setCurvePrecisions({});
      setCurveUnits({});
      setConversionRevision((revision) => revision + 1);
      return;
    }

    let cancelled = false;

    const loadConversions = async () => {
      const measureEntries = await Promise.all(
        curveNames.map(async (curveName) => {
          try {
            const curveMeasure = (await dispatch(
              apiSlice.endpoints.get.initiate(
                `${systemsEP}/${unitSystemId}/curve/${encodeURIComponent(
                  curveName
                )}/measure`,
                { forceRefetch: true, subscribe: false }
              )
            ).unwrap()) as CurveMeasure;

            return [makeCurveKey(curveName), curveMeasure] as const;
          } catch {
            return undefined;
          }
        })
      );

      if (cancelled) return;

      const measures = new Map(
        measureEntries.filter(
          (entry): entry is NonNullable<typeof entry> => Boolean(entry)
        )
      );

      const conversionEntries = await Promise.all(
        conversionRequests.map(async ({ curveName, unitName }) => {
          const targetUnit = getCurveMeasureUnit(measures.get(makeCurveKey(curveName)));
          if (!targetUnit) return undefined;

          const encodedCurveName = encodeURIComponent(curveName);
          const encodedUnitName = encodeURIComponent(unitName);
          const key = makeConversionKey(curveName, unitName);

          try {
            const curveConvertFormula = (await dispatch(
              apiSlice.endpoints.get.initiate(
                `${systemsEP}/${unitSystemId}/curve/${encodedCurveName}${convertFormulaEP}?fromLabel=${encodedUnitName}`,
                { forceRefetch: true, subscribe: false }
              )
            ).unwrap()) as string;

            const expression = compileFormula(curveConvertFormula);
            if (!expression) return undefined;

            return [
              key,
              {
                curveName,
                sourceUnit: unitName,
                targetUnit,
                expression,
              },
            ] as const;
          } catch {
            return undefined;
          }
        })
      );

      if (cancelled) return;

      setConversions(
        Object.fromEntries(conversionEntries.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)))
      );
      setCurvePrecisions(
        Object.fromEntries(
          Array.from(measures.entries())
            .map(([curveKey, curveMeasure]) => {
              const precision = getCurveMeasurePrecision(curveMeasure);
              return precision === undefined
                ? undefined
                : ([curveKey, precision] as const);
            })
            .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
        )
      );
      setCurveUnits(
        Object.fromEntries(
          Array.from(measures.entries())
            .map(([curveKey, curveMeasure]) => {
              const unit = getCurveMeasureUnit(curveMeasure);
              return unit ? ([curveKey, unit] as const) : undefined;
            })
            .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
        )
      );
      setConversionRevision((revision) => revision + 1);
    };

    loadConversions();

    return () => {
      cancelled = true;
    };
  }, [
    curveRequestsKey,
    convertFormulaEP,
    dispatch,
    systemsEP,
    unitSystemId,
  ]);

  const convertedLogsData = useMemo(() => {
    if (!unitSystemId || Object.keys(conversions).length === 0) {
      return logsData;
    }

    return Object.fromEntries(
      Object.entries(logsData).map(([logUid, logData]) => {
        if (!logData) return [logUid, logData];

        const convertedSpecs = logData.curveSpecifications?.map((spec) => {
          const unitName = normalizeUnitName(spec.unit);
          const conversion = conversions[makeConversionKey(spec.mnemonic, unitName)];
          return conversion ? { ...spec, unit: conversion.targetUnit } : spec;
        });

        const convertedRows = logData.data?.map((row) => {
          let convertedRow: Record<string, unknown> | undefined;

          logData.curveSpecifications?.forEach((spec) => {
            const unitName = normalizeUnitName(spec.unit);
            const conversion = conversions[makeConversionKey(spec.mnemonic, unitName)];
            if (!conversion || !(spec.mnemonic in row)) return;

            if (!convertedRow) convertedRow = { ...row };
            convertedRow[spec.mnemonic] = convertValue(row[spec.mnemonic], conversion);
          });

          return convertedRow || row;
        });

        return [
          logUid,
          {
            ...logData,
            curveSpecifications: convertedSpecs || logData.curveSpecifications,
            data: convertedRows || logData.data,
          },
        ];
      })
    ) as LogsDataByUid;
  }, [conversions, logsData, unitSystemId]);

  const convertedOverlaySeries = useMemo(() => {
    if (!unitSystemId || Object.keys(conversions).length === 0) {
      return overlaySeries;
    }

    return overlaySeries.map((overlay) => {
      const unitName = normalizeUnitName(overlay.unit);
      const depthUnitName = normalizeUnitName(overlay.depthUnit);
      const conversion =
        conversions[makeConversionKey(overlay.mnemonic, unitName)];
      const depthConversion =
        conversions[
          makeConversionKey(OVERLAY_DEPTH_CURVE_NAME, depthUnitName)
        ];
      if (!conversion && !depthConversion) return overlay;

      return {
        ...overlay,
        unit: conversion ? conversion.targetUnit : overlay.unit,
        depthUnit: depthConversion
          ? depthConversion.targetUnit
          : overlay.depthUnit,
        points: overlay.points.map((point) => ({
          ...point,
          value: convertValue(point.value, conversion) as number,
          md: convertValue(point.md, depthConversion) as number,
          tvd:
            point.tvd === undefined
              ? point.tvd
              : (convertValue(point.tvd, depthConversion) as number),
        })),
      };
    });
  }, [conversions, overlaySeries, unitSystemId]);

  // Point conversion for values that do not come from a log (well geometry and
  // the like): same formulas, applied one value at a time.
  const convertCurveValue = useCallback(
    (curveName: string, sourceUnit: string | undefined, value: number) => {
      const conversion =
        conversions[makeConversionKey(curveName, normalizeUnitName(sourceUnit))];
      return conversion ? (convertValue(value, conversion) as number) : value;
    },
    [conversions]
  );

  return {
    convertedLogsData,
    convertedOverlaySeries,
    // Decimal places per curve (lower-cased mnemonic), as reported by the
    // measure endpoint of the active unit system.
    curvePrecisions,
    // Target unit label per curve (lower-cased mnemonic), from the same endpoint.
    curveUnits,
    conversionRevision,
    convertCurveValue,
  };
};
